/**
 * Component: Free shipping notification script
 * ------------------------------------------------------------------------------
 * A re-usable snippet to display the amount left to spend for free shipping.
 * - This is purely cosmetic and does not control the shipping rates in the admin.
 *
 * @namespace freeShippingNotification
 */
import {formatMoney} from '@shopify/theme-currency';
import cssClasses from '../helpers/cssClasses';

/**
 * DOM selectors.
 */
const selectors = {
  progressBar: '[js-free-shipping-notification="progressBar"]',
  container: '[js-free-shipping-notification="container"]',
  text: '[js-free-shipping-notification="text"]',
};

/**
 * Create a new free shipping notification object.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    progressBar: [...document.querySelectorAll(selectors.progressBar)],
    container: [...document.querySelectorAll(selectors.container)],
    text: [...document.querySelectorAll(selectors.text)],
  };

  /**
   * Initialise component.
   */
  function init() {
    setEventBusListeners();
  }

  /**
   * Set listeners to update on every cart change.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('AjaxCart:updated', (response) => update(response));
  }

  /**
   * Update free shipping notification.
   * @param {Object} cart - Current cart.
   */
  function update(cart) {
    render(cart);

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }
  }

  /**
   * Calculates total for no shipping items (digital products).
   * @param {Object} cart - Current cart.
   */
  function getNoShippingTotal(cart) {
    return cart.items.reduce((accumulator, currentValue) => currentValue.requires_shipping ? accumulator + currentValue.final_price * currentValue.quantity : accumulator, 0);
  }

  /**
   * Update notification template using new values.
   * @param {Object} cart - Current cart.
   */
  function render(cart) {
    const threshold = getThreshold();
    const noShippingTotal = getNoShippingTotal(cart);
    const difference = threshold - noShippingTotal;
    const messages = theme.freeShipping.messages;
    const progress = 100 - ((difference / threshold) * 100);

    if (difference <= 0) {
      nodeSelectors.text.forEach((element) => (element.innerHTML = messages.ideal));


      if (theme.freeShipping.progressBar) {
        nodeSelectors.progressBar.forEach((element) => {
          element.style.maxWidth = `100%`;
        });
      }

      return;
    }

    if (noShippingTotal < threshold && noShippingTotal > 0) {

      nodeSelectors.text.forEach((element) => {
        element.innerHTML = messages.partial.replace('#value#', getValueTemplate(difference));
      });

      if (theme.freeShipping.progressBar) {
        nodeSelectors.progressBar.forEach((element) => {
          element.style.maxWidth = `${progress}%`;
        });
      }

      return;
    }

    nodeSelectors.text.forEach((element) => {
      element.innerHTML = messages.blank.replace('#value#', getValueTemplate(threshold));
    });


    if (theme.freeShipping.progressBar) {
      nodeSelectors.progressBar.forEach((element) => {
        element.style.maxWidth = `0%`;
      });
    }
  }

  /**
   * Get value template with currency data selectors.
   * @param {Number} price - Price to store in price data attribute.
   * @returns {String}
   */
  function getValueTemplate(price) {
    return `<span class="threshold" js-currency="price" data-price="${price}">${getFormattedCurrency(price)}</span>`;
  }

  /**
   * Get formatted currency.
   * @param {Number} cents - Raw amount to convert before formatting.
   * @returns {String}
   */
  function getFormattedCurrency(cents) {
    return formatMoney(cents, theme.moneyFormat).replace('.00', '');
  }

  /**
   * Get free shipping threshold value.
   * @returns {Number}
   */
  function getThreshold() {
    if (typeof theme.freeShipping.threshold !== 'number') {
      return Number(theme.freeShipping.threshold);
    }

    return theme.freeShipping.threshold;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
